import NotLoggedInContainer from '../base/NotLoggedInContainer'
import { Checkbox, styled, Typography, useMediaQuery } from '@mui/material'
import ErrorMessage from '../base/ErrorMessage'
import LinkButton from '../base/buttons/LinkButton'
import notLoggedInPages from '../../navigation/notLoggedInPages'
import TextInput from '../base/TextInput'
import PasswordTextInput from '../base/PasswordTextInput'
import OpaqueButton from '../base/buttons/OpaqueButton'
import { useEffect, useState } from 'react'
import { useSetPassword } from '../../hooks/useSetPassword'
import PasswordValidator from '../base/PasswordValidator'
import { validateEmail } from '../../utils/utils'
import { useRouter } from 'next/router'
import { createStudentAccount, createTeacherAccount, getValidationTokenData } from '../../requests/notLoggedInRequests'
import ConfirmYourEmail from './ConfirmYourEmail'
import redirectToHomepage from '../../logic/redirectToHomepage'
import LinkExpired from './LinkExpired'

const Container = styled('div')({
  padding: '32px 16px 120px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const LogInWindow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  boxShadow: '0 0 12px rgba(0, 0, 0, 0.8)',
  width: '100%',
  maxWidth: `${450 + 474}px`,
  [theme.breakpoints.down('md')]: {
    maxWidth: '450px',
  },
}))

const LogInForm = styled('form')({
  maxWidth: '450px',
  width: '100%',
  padding: '24px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  rowGap: '12px',
})

const VisualSplash = styled('div')(({ theme }) => ({
  backgroundImage: `url(/static/sign-up-background.png), linear-gradient(${theme.palette.loginHeroBackground.gradientStart}, ${theme.palette.loginHeroBackground.gradientEnd})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '474px',
  minHeight: '600px',
}))

const SplashText = styled(Typography)(({ theme }) => ({
  color: theme.palette.mainText.fullWhite,
}))

const SignUpTextField = styled(TextInput)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.mainText.main,
    },
  },
}))

const NameTextFields = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  columnGap: '8px',
})

const SignUpPasswordTextField = styled(PasswordTextInput)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.mainText.main,
    },
  },
}))

const CreateAccountButton = styled(OpaqueButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.darker,
  width: '100%',
  borderRadius: '24px',
  '&& .MuiTouchRipple-child': {
    backgroundColor: theme.palette.background.darker,
    filter: 'brightness(85%)',
  },
  '&:hover': {
    backgroundColor: theme.palette.background.darker,
    filter: 'brightness(65%)',
  },
}))

const StyledPasswordValidator = styled(PasswordValidator)({
  alignSelf: 'flex-start',
})

const CheckboxRow = styled('div')({
  display: 'flex',
  columnGap: '2px',
  alignItems: 'center',
  alignSelf: 'flex-start',
  marginLeft: '-10px',
})

const CheckboxLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
})

const StudentSignUp = ({ teacher }) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const [genericError, setGenericError] = useState('')
  const [readTerms, setReadTerms] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [emailError, setEmailError] = useState('')
  const [inviteCodeError, setInviteCodeError] = useState('')
  const [requestInProgress, setRequestInProgress] = useState(false)
  const [signUpSuccess, setSignUpSuccess] = useState(false)
  const [linkExpired, setLinkExpired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dontAllowSubmitError, setDontAllowSubmitError] = useState('')
  const router = useRouter()
  const { validationToken } = router.query
  const [
    newPassword,
    newPasswordConfirmation,
    containsNumber,
    containsLetter,
    containsAtLeast8Char,
    passwordsMatch,
    passwordIsValid,
    setNewPassword,
    setNewPasswordConfirmation,
  ] = useSetPassword()

  useEffect(() => {
    (async () => {
      if (teacher && validationToken) {
        setLoading(true)
        try {
          const [success, teacherData] = await getValidationTokenData(validationToken)
          if (success) {
            setEmail(teacherData.emailAddress)
            setFirstName(teacherData.firstName)
            setLastName(teacherData.lastName)
          } else if (teacherData === 'validationToken is invalid') {
            setDontAllowSubmitError('This is an invalid invite URL. Please check the link we sent you and try again.')
          } else if (teacherData === 'validationToken has already been validated') {
            setLinkExpired(true)
          } else {
            setDontAllowSubmitError('There was a problem pulling your teacher account. Please refresh the page and try again.')
          }
        } catch (e) {
          setDontAllowSubmitError('There was a problem pulling your teacher account. Please refresh the page and try again.')
        } finally {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    })()
  }, [validationToken])

  const canSubmit = () => {
    if (!teacher && !inviteCode) {
      return false
    }
    if (!email) {
      return false
    }
    if (!firstName) {
      return false
    }
    if (!lastName) {
      return false
    }
    if (!passwordIsValid) {
      return false
    }
    if (!readTerms) {
      return false
    }
    return true
  }

  const handleAccountCreationError = (payload) => {
    if (payload.path === 'emailAddress') {
      setEmailError(payload.message)
    } else if (payload.path === 'code') {
      setInviteCodeError(payload.message)
    } else {
      setGenericError(payload?.message || 'There was an error creating your account, please try again.')
    }
  }

  const submitCreateAccount = async (e) => {
    e.preventDefault()
    if (!canSubmit()) {
      return
    }
    setRequestInProgress(true)
    try {
      let success
      let payload
      let token
      if (!teacher) {
        [success, payload] = await createStudentAccount({
          code: inviteCode,
          emailAddress: email,
          firstName,
          lastName,
          password: newPassword
        })
        if (success) {
          setSignUpSuccess(true)
          return
        }
      } else {
        [success, token] = await createTeacherAccount({
          validationToken,
          emailAddress: email,
          firstName,
          lastName,
          password: newPassword
        })
        if (success) {
          localStorage.setItem('access_token', token)
          await redirectToHomepage(router)
          return
        }
      }
      handleAccountCreationError(payload)
    } catch (e) {
      setGenericError('There was an error creating your account. Please try again.')
    } finally {
      setRequestInProgress(false)
    }
  }

  if (signUpSuccess) {
    return <ConfirmYourEmail
      title={'Confirm Your Email'}
      body={'To access your account, you need to confirm your email address.' +
        ' Check your inbox for the email we just sent you, and then click' +
        ' the link inside.'}
    />
  }

  if (linkExpired) {
    return <LinkExpired />
  }

  return (
    <NotLoggedInContainer student={!teacher} loading={loading} type={'sign-up'}>
      <Container>
        <LogInWindow>
          <LogInForm>
            <Typography variant={'h5'} sx={{ marginBottom: '12px', fontWeight: 'bold' }}>
              Sign Up
            </Typography>
            <ErrorMessage errorMessage={dontAllowSubmitError} />
            { !teacher && <SignUpTextField
              variant={'outlined'}
              label={'Invite code'}
              width={'100%'}
              value={inviteCode}
              errorMessage={inviteCodeError}
              onChange={(e) => {
                setInviteCodeError('')
                setInviteCode(e.target.value)
              }}
            /> }
            <SignUpTextField
              variant={'outlined'}
              label={'Email'}
              width={'100%'}
              disabled={teacher}
              value={email}
              errorMessage={emailError}
              onBlur={() => {
                if (email && !validateEmail(email)) {
                  setEmailError('Please enter a valid email address')
                }
              }}
              onChange={(e) => {
                setEmailError('')
                setEmail(e.target.value)
              }}
            />
            <NameTextFields>
              <SignUpTextField
                variant={'outlined'}
                label={'First name'}
                width={'100%'}
                disabled={!!dontAllowSubmitError}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value)
                }}
              />
              <SignUpTextField
                variant={'outlined'}
                label={'Last name'}
                width={'100%'}
                value={lastName}
                disabled={!!dontAllowSubmitError}
                onChange={(e) => {
                  setLastName(e.target.value)
                }}
              />
            </NameTextFields>
            <SignUpPasswordTextField
              variant={'outlined'}
              label={'Create your password'}
              width={'100%'}
              value={newPassword}
              disabled={!!dontAllowSubmitError}
              onChange={(e) => {
                setNewPassword(e.target.value)
              }}
            />
            <SignUpPasswordTextField
              variant={'outlined'}
              label={'Confirm your password'}
              width={'100%'}
              value={newPasswordConfirmation}
              disabled={!!dontAllowSubmitError}
              onChange={(e) => {
                setNewPasswordConfirmation(e.target.value)
              }}
            />
            <StyledPasswordValidator
              containsLetter={containsLetter}
              containsNumber={containsNumber}
              atLeast8Characters={containsAtLeast8Char}
              passwordMatch={passwordsMatch}
            />
            <CheckboxRow>
              <Checkbox
                checked={readTerms}
                label={'signUpCheckbox'}
                data-testid={'signUpCheckbox'}
                onChange={(e) => {
                  setReadTerms(e.target.checked)
                }}
              />
              <CheckboxLabel>
                <Typography variant={'body2'} display={'inline'}>
                  I agree to the&nbsp;
                </Typography>
                <LinkButton
                  title={'Terms of Service'}
                  inline
                  onClick={() => {
                    window.open(notLoggedInPages.termsOfUse.getRoute(), '_blank')
                  }}
                />
                <Typography variant={'body2'} display={'inline'}>
                  &nbsp;&&nbsp;
                </Typography>
                <LinkButton
                  title={'Privacy Policy'}
                  onClick={() => {
                    window.open(notLoggedInPages.privacyPolicy.getRoute(), '_blank')
                  }}
                  inline />
              </CheckboxLabel>
            </CheckboxRow>
            <ErrorMessage errorMessage={genericError} />
            <ErrorMessage errorMessage={dontAllowSubmitError} />
            <CreateAccountButton disabled={!!dontAllowSubmitError || !canSubmit()} title={'Create An Account'} loading={requestInProgress} type={'submit'} onClick={submitCreateAccount}/>
          </LogInForm>
          {!isSmall && <VisualSplash>
            { !teacher && <>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              Discover
            </SplashText>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              proven
            </SplashText>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              strategies to
            </SplashText>
            </> }
            { teacher && <>
              <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
                Help students
              </SplashText>
            </> }
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              increase
            </SplashText>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              focus and
            </SplashText>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              reduce stress
            </SplashText>
          </VisualSplash>}
        </LogInWindow>
      </Container>
    </NotLoggedInContainer>

  )
}

export default StudentSignUp
